import React, { Fragment } from 'react'

import {
  DialogTitle,
  DialogContent,
  DialogActions,
  Stack,
  Button,
  IconButton,
  Typography,
  Divider,
  Box,
  alpha,
  Drawer,
} from '@mui/material'
import { Close as CloseIcon } from '@mui/icons-material'

import { useDisclosure } from '@/hooks/useDisclosure'

import ChargingSessionSummary from '../components/ChargingSessionSummary'
import { getElapsedTime } from '../utils/getElapsedTime'
import { timeUtils } from '@/utils/time'
import { chargingUtils } from '@/utils/charging'
import { numericUtils } from '@/utils/numeric'
import { ChargingPaymentMethod, PaymentLabels, PaymentMethodEnum, PaymentMethods } from '@/types/Payment'
import { ChargingSummary } from '@/types/Charging'
import theme from '@/theme'
import { SectionHeader, SummaryRow } from './ChargingSessionSummaryParts'

export interface ChargingSessionSummaryModalProps {
  modalTitle?: string
  modalMethods: ReturnType<typeof useDisclosure<ChargingSummary>>
  onClose?: () => Promise<void>
}

export const ChargingSessionSummaryModal: React.FC<ChargingSessionSummaryModalProps> = ({
  modalTitle = 'Histórico da sessão',
  modalMethods,
  onClose,
}) => {
  const { isOpen, data: payload } = modalMethods

  const chargeStartTime = chargingUtils.getChargeStartTime(payload)
  const chargeEndTime = chargingUtils.getChargeEndTime(payload)

  const elapsedTime = getElapsedTime(
    chargeStartTime ? new Date(chargeStartTime) : null,
    chargeEndTime ? new Date(chargeEndTime) : new Date()
  )

  const idleEndTime = timeUtils.calculateEndTimeFromMinutes(payload?.idle_start_time, payload?.idle_time_minutes)

  const hasIdleTime = Boolean(payload?.idle_time_minutes && payload.idle_time_minutes > 0)
  const hasSessionTail = chargingUtils.hasSessionTail(payload)

  const idleElapsedTime =
    hasIdleTime && payload?.idle_start_time ? getElapsedTime(new Date(payload.idle_start_time), idleEndTime) : undefined

  const getPaymentLabel = (method: ChargingPaymentMethod | null | undefined): string => {
    if (!method) return 'N/A'
    return PaymentLabels[method] || 'N/A'
  }

  const hasIdleCostInfo = numericUtils.hasValue(payload?.idle_cost)
  const hasActivationFeeCost = Number(payload?.activation_fee_cost ?? 0) > 0

  const paymentWalletValue = payload?.payment_wallet_value ?? 0
  const paymentExternalValue = payload?.payment_external_value ?? 0
  const paymentTotal = payload?.payment_amount ?? 0

  const refundedToWallet = Number(payload?.refunded_to_wallet ?? 0)
  const refundedExternally = Number(payload?.refunded_externally ?? 0)
  const refundTotal = payload?.refund_amount ?? 0
  const hasPendingRefund = Number(payload?.pending_external_refund ?? 0) > 0

  const showExternalPayment = (payload?.payment_method as PaymentMethods) !== PaymentMethodEnum.CREDITS

  // Tarifa média por kWh: kwh_cost é o custo TOTAL de energia da sessão, não a tarifa.
  // Divide pela energia entregue para obter a média (exata quando a sessão fica numa
  // única janela; ponderada quando cruza janelas de tarifa dinâmica).
  const kwhDelivered = payload?.kwh_delivered ?? 0
  const kwhCost = payload?.kwh_cost != null ? Number(payload.kwh_cost) : null
  const averageCostPerKwh = kwhCost !== null && kwhDelivered > 0 ? kwhCost / kwhDelivered : null

  // Detalhamento por janela de tarifa dinâmica: o backend só preenche quando mais de
  // uma tarifa incidiu na sessão (comum em AC, onde a recarga dura horas e cruza janelas).
  const tariffBreakdown = payload?.tariff_breakdown ?? null
  const hasTariffBreakdown = !!tariffBreakdown && tariffBreakdown.length > 1

  const handleClose = async () => {
    modalMethods?.close()
    await onClose?.()
  }

  return (
    <Drawer
      open={isOpen}
      anchor="bottom"
      slotProps={{
        paper: { sx: { padding: 0.5, maxHeight: '95svh', borderTopLeftRadius: 16, borderTopRightRadius: 16 } },
      }}
      onClose={handleClose}
    >
      <IconButton
        aria-label="fechar"
        onClick={handleClose}
        sx={{
          position: 'absolute',
          right: 4,
          top: 4,
        }}
      >
        <CloseIcon color="disabled" sx={{ fontSize: '16px' }} />
      </IconButton>

      <DialogTitle sx={{ pb: 2 }}>{modalTitle}</DialogTitle>

      <DialogContent>
        <Stack spacing={2}>
          <Box display="flex" justifyContent="center">
            <ChargingSessionSummary
              elapsedTime={elapsedTime}
              idleElapsedTime={idleElapsedTime}
              kwhDelivered={payload?.kwh_delivered}
              totalCost={payload?.total_cost}
              idleCost={payload?.idle_cost}
            />
          </Box>

          <Divider />

          {payload?.summary && (
            <Box
              sx={{
                p: 1.5,
                borderRadius: 1,
                backgroundColor: alpha(theme.palette.primary.main, 0.05),
                border: '1px dashed',
                borderColor: alpha(theme.palette.primary.main, 0.2),
                mb: 1,
              }}
            >
              <Typography variant="caption" color="primary" fontWeight="bold" display="block" mb={0.5}>
                RESUMO DO EVENTO:
              </Typography>
              <Typography variant="body2" color="text.primary" sx={{ fontStyle: 'italic' }}>
                "{payload.summary}"
              </Typography>
            </Box>
          )}

          <Stack spacing={0.5}>
            <SectionHeader title="Detalhes da sessão" />
            <SummaryRow
              label="Início"
              value={timeUtils.formatDateWithTime(chargeStartTime, 'N/A')}
              tooltip="Data e hora em que o carregamento começou."
            />
            <SummaryRow
              label="Fim da carga"
              value={timeUtils.formatDateWithTime(chargeEndTime, 'N/A')}
              tooltip="Data e hora em que o carregamento terminou. Não inclui o tempo de ociosidade."
            />
            {hasSessionTail && (
              <SummaryRow
                label="Fim da sessão"
                value={timeUtils.formatDateWithTime(payload?.end_time, 'N/A')}
                tooltip="Data e hora em que a vaga foi liberada. O intervalo desde o fim da carga é o tempo de ociosidade."
              />
            )}
            <SummaryRow
              label="kWh consumidos"
              value={`${numericUtils.formatKwh(payload?.kwh_delivered)} kWh`}
              tooltip="Quantidade total de energia entregue ao veículo durante a sessão."
            />
            <SummaryRow
              label={hasTariffBreakdown ? 'Custo médio por kWh' : 'Custo por kWh'}
              value={numericUtils.formatNumberToBRLCurrency(averageCostPerKwh, 'N/A')}
              tooltip={
                hasTariffBreakdown
                  ? 'Tarifa média desta sessão. A recarga passou por mais de uma faixa de tarifa — veja o detalhamento abaixo.'
                  : 'Tarifa cobrada por cada kWh consumido nesta sessão.'
              }
            />
            {hasTariffBreakdown &&
              tariffBreakdown!.map((entry, index) => (
                <SummaryRow
                  key={`${entry.label}-${entry.rate}-${index}`}
                  label={`• ${entry.label}`}
                  value={`${numericUtils.formatNumberToBRLDecimal(Number(entry.kwh))} kWh × ${numericUtils.formatNumberToBRLCurrency(
                    entry.rate
                  )} = ${numericUtils.formatNumberToBRLCurrency(entry.cost)}`}
                  tooltip="Parcela do custo de energia cobrada nesta faixa de tarifa."
                />
              ))}
            {hasActivationFeeCost && (
              <SummaryRow
                label="Taxa de ativação"
                value={numericUtils.formatNumberToBRLCurrency(payload?.activation_fee_cost, 'N/A')}
                tooltip="Valor fixo cobrado por iniciar a recarga neste carregador."
              />
            )}
          </Stack>

          <Divider />

          <Stack spacing={0.5}>
            <SectionHeader title="Carteira" />
            <SummaryRow
              label="Saldo antes da sessão"
              value={numericUtils.formatNumberToBRLCurrency(payload?.wallet_balance_before, 'N/A')}
              tooltip="Saldo disponível na carteira no momento em que a sessão foi iniciada."
            />
            <SummaryRow
              label="Possuía saldo pendente?"
              value={payload?.had_pending_balance ? 'Sim' : 'Não'}
              tooltip="Indica se havia valores pendentes de sessões anteriores no momento do início desta sessão."
            />
            {payload?.had_pending_balance && (
              <SummaryRow
                label="Saldo pendente"
                value={numericUtils.formatNumberToBRLCurrency(payload?.pending_balance, 'N/A')}
                color="warning.main"
                tooltip="Valor pendente de sessões anteriores que foi descontado nesta sessão."
              />
            )}
            <SummaryRow
              label="Usou saldo da carteira?"
              value={payload?.used_wallet_balance ? 'Sim' : 'Não'}
              tooltip="Indica se o saldo da carteira foi utilizado para cobrir parte ou todo o custo da sessão."
            />
            <SummaryRow
              label="Saldo efetivo na carteira"
              value={numericUtils.formatNumberToBRLCurrency(payload?.effective_wallet_balance, 'N/A')}
              bold
              tooltip="Saldo real disponível na carteira, descontando eventuais valores pendentes de sessões anteriores."
            />
          </Stack>

          <Divider />

          <Stack spacing={0.5}>
            <SectionHeader title="Pagamento" />
            <SummaryRow
              label="Método de pagamento"
              value={getPaymentLabel(payload?.payment_method)}
              tooltip="Forma de pagamento utilizada para cobrir o custo da sessão (ex: cartão de crédito, Pix)."
            />
            <SummaryRow
              label="Carteira"
              value={numericUtils.formatNumberToBRLCurrency(paymentWalletValue)}
              tooltip="Valor descontado do saldo da carteira para cobrir parte ou todo o custo da sessão."
            />
            {showExternalPayment && (
              <SummaryRow
                label={getPaymentLabel(payload?.payment_method)}
                value={numericUtils.formatNumberToBRLCurrency(paymentExternalValue)}
                tooltip="Valor cobrado no método de pagamento externo (cartão de crédito ou Pix)."
              />
            )}
            <SummaryRow
              label="Valor total"
              value={numericUtils.formatNumberToBRLCurrency(paymentTotal)}
              bold
              tooltip="Soma do valor debitado da carteira com o valor cobrado no método de pagamento externo."
            />
          </Stack>

          {hasIdleCostInfo && (
            <Fragment>
              <Divider />

              <Stack spacing={0.5}>
                <SectionHeader title="Ociosidade" color="error" />
                <SummaryRow
                  label="Tempo de tolerância"
                  value={
                    payload?.idle_tolerance_time === 0
                      ? '0'
                      : timeUtils.formatMinutesToTime(payload?.idle_tolerance_time) || 'N/A'
                  }
                  tooltip="Período de tolerância após o fim do carregamento antes de iniciar a cobrança por ociosidade."
                />
                <SummaryRow
                  label="Início da ociosidade"
                  value={timeUtils.formatDateWithTime(payload?.idle_start_time, 'N/A')}
                  tooltip="Momento em que o veículo passou a ser considerado ocioso no ponto de carregamento."
                />
                <SummaryRow
                  label="Fim da ociosidade"
                  value={idleEndTime ? timeUtils.formatDateWithTime(idleEndTime, 'N/A') : 'N/A'}
                  tooltip="Momento em que o veículo foi desconectado ou a ociosidade foi encerrada."
                />
                <SummaryRow
                  label="Tempo decorrido"
                  value={idleElapsedTime}
                  tooltip="Tempo total em que o veículo permaneceu ocioso no ponto de carregamento."
                />
                <SummaryRow
                  label="Custo por minuto"
                  value={numericUtils.formatNumberToBRLCurrency(payload?.idle_rate, 'N/A')}
                  tooltip="Tarifa cobrada por cada minuto de ociosidade após o tempo de tolerância."
                />
                <SummaryRow
                  label="Valor cobrado"
                  value={numericUtils.formatNumberToBRLCurrency(payload?.idle_cost, 'N/A')}
                  color="error"
                  bold
                  tooltip="Valor total cobrado pela ociosidade do veículo no ponto de carregamento."
                />
              </Stack>
            </Fragment>
          )}

          <Divider />

          <Stack spacing={0.5}>
            <SectionHeader title="Reembolso" />
            <SummaryRow
              label="Carteira"
              value={numericUtils.formatNumberToBRLCurrency(refundedToWallet)}
              tooltip="Valor reembolsado para a carteira."
            />
            {!!showExternalPayment && (
              <SummaryRow
                label={
                  hasPendingRefund
                    ? `${getPaymentLabel(payload?.payment_method)} (em devolução)`
                    : getPaymentLabel(payload?.payment_method)
                }
                value={numericUtils.formatNumberToBRLCurrency(refundedExternally)}
                color={hasPendingRefund ? 'warning.main' : undefined}
                tooltip={
                  hasPendingRefund
                    ? 'Devolução solicitada e ainda não concluída.'
                    : 'Valor reembolsado para o método de pagamento externo (cartão de crédito ou Pix).'
                }
              />
            )}
            <SummaryRow
              label={hasPendingRefund ? 'Total do reembolso' : 'Total reembolsado'}
              value={numericUtils.formatNumberToBRLCurrency(refundTotal)}
              bold
              tooltip="Valor total a ser reembolsado caso o consumo tenha sido menor que o valor pago inicialmente."
            />
          </Stack>

          <Divider />

          <Stack spacing={0.5}>
            <SectionHeader title="Valores pendentes" />
            <SummaryRow
              label="Valor pendente após a recarga"
              value={numericUtils.formatNumberToBRLCurrency(payload?.pending_amount_after)}
              color={numericUtils.hasValue(payload?.pending_amount_after) ? 'warning.main' : undefined}
              tooltip="Valor pendente após esta sessão (ex: custo de ociosidade)."
            />
          </Stack>

          <Divider />

          <Stack spacing={0.5}>
            <SectionHeader title="Saldo final" />
            <SummaryRow
              label="Saldo final da carteira"
              value={numericUtils.formatNumberToBRLCurrency(payload?.final_wallet_balance, 'N/A')}
              bold
              tooltip="Saldo da carteira após todos os débitos e créditos desta sessão serem aplicados."
            />
          </Stack>
        </Stack>
      </DialogContent>

      <DialogActions sx={{ pb: 2 }}>
        <Button
          color="primary"
          variant="contained"
          onClick={handleClose}
          sx={{ width: '100%', borderRadius: 4 }}
          disableElevation
        >
          Fechar
        </Button>
      </DialogActions>
    </Drawer>
  )
}
